import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { platformAuthApi } from './platformApi.js';
import { setPlatformAccessToken, setPlatformUnauthorizedHandler } from './platformClient.js';
import { PLATFORM_STORAGE_KEY } from './platformSession.js';

/**
 * Sitzung der Anbieterebene.
 *
 * Eigener Speicherplatz, eigener Client, eigener Abmelde-Rückruf. Damit
 * können beide Hüte gleichzeitig im selben Browser aufliegen: die normale
 * Buchhaltungssitzung und die Anbietersicht stören sich nicht, und ein
 * abgelaufenes Anbietertoken loggt niemanden aus seiner Buchhaltung aus.
 */

const STORAGE_KEY = PLATFORM_STORAGE_KEY;

export const PlatformAuthContext = createContext(null);

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) {
    throw new Error('usePlatformAuth muss innerhalb von PlatformAuthProvider benutzt werden');
  }
  return ctx;
}

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistSession(session) {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/* Auffrischungstokens sind einmalig verwendbar, und StrictMode lässt den
   Starteffekt doppelt laufen. Ohne Bündelung würde der zweite Aufruf das
   bereits eingelöste Token vorlegen und ein 401 kassieren - also sofort
   abmelden. Dasselbe Muster wie im Mandantenpfad (AuthContext.jsx). */
let inflightRefresh = null;

function dedupedRefresh(refreshToken) {
  if (inflightRefresh?.token === refreshToken) {
    return inflightRefresh.promise;
  }
  const promise = platformAuthApi.refresh(refreshToken).then((result) => {
    persistSession({
      account: result.account,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    });
    return result;
  });
  inflightRefresh = { token: refreshToken, promise };
  promise
    .catch(() => {})
    .finally(() => {
      if (inflightRefresh?.promise === promise) {
        inflightRefresh = null;
      }
    });
  return promise;
}

export function PlatformAuthProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());
  const [isBootstrapping, setIsBootstrapping] = useState(Boolean(loadSession()));

  const applySession = useCallback((next) => {
    setSession(next);
    setPlatformAccessToken(next?.accessToken || null);
    persistSession(next);
  }, []);

  // Token beim Laden gegen ein frisches tauschen, damit ein Neuladen nicht
  // jedes Mal eine neue Anmeldung verlangt.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const existing = loadSession();
      if (!existing?.refreshToken) {
        setIsBootstrapping(false);
        return;
      }
      try {
        const result = await dedupedRefresh(existing.refreshToken);
        if (!cancelled) {
          applySession({
            account: result.account,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }
      } catch {
        if (!cancelled) {
          applySession(null);
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const signIn = useCallback(
    async (email, password) => {
      const result = await platformAuthApi.login(email, password);
      applySession({
        account: result.account,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
      return result.account;
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    const current = session;
    applySession(null);
    if (current?.refreshToken) {
      try {
        await platformAuthApi.logout(current.refreshToken);
      } catch {
        // Die lokale Sitzung ist schon weg; mehr ist hier nicht nötig.
      }
    }
  }, [session, applySession]);

  useEffect(() => {
    setPlatformUnauthorizedHandler(() => applySession(null));
    return () => setPlatformUnauthorizedHandler(null);
  }, [applySession]);

  const value = useMemo(
    () => ({
      account: session?.account || null,
      roles: session?.account?.roles || [],
      permissions: session?.account?.permissions || {},
      isAuthenticated: Boolean(session?.accessToken),
      isBootstrapping,
      signIn,
      signOut,
    }),
    [session, isBootstrapping, signIn, signOut],
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}
